import React, { memo, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import classNames from 'classnames';
import { gql } from '@apollo/client';
import { mapCursorToMax } from 'map-cursor-to-max';
import { Clickable, useBreakpoint } from '@moda/om';
import { compact } from '@moda/portal-stanchions';
import { ProductZoomedGallery, PRODUCT_ZOOMED_GALLERY_FRAGMENT } from '../ProductZoomedGallery';
import {
  DesktopProductMediaGalleryFragment,
  DesktopProductMediaGalleryVariantFragment
} from '../../generated/types';
import { getActiveVariantBy } from '../../lib/getActiveVariantBy';
import { useList } from '../../hooks/useList';
import { Image } from '../Image';
import { usePageType } from '../../hooks/usePageType';
import { tc } from '../../lib/trackingContext';
import { ProductDetailPageVideo, PRODUCT_VIDEOS_FRAGMENT } from '../ProductDetailPageVideo';
import { PENDING_SRC } from '../../constants/pendingSrc';
import { VIDEO_THUMBNAIL_POSITION } from '../../constants';
import { DesktopProductMediaGalleryImageThumb } from './DesktopProductMediaGalleryImageThumb';
import { useDesktopProductMediaGalleryImageThumbnails } from './useDesktopProductMediaGalleryImageThumbnails';
import { DesktopProductMediaGalleryVideoThumb } from './DesktopProductMediaGalleryVideoThumb';

import './DesktopProductMediaGallery.scss';

export enum DesktopProductMediaGalleryThumbType {
  Image = 'image',
  Video = 'video'
}

type ImageThumbnail = {
  src: string;
  key: string;
  large: string | null;
  type: DesktopProductMediaGalleryThumbType.Image;
  thumb: string;
};

type VideoThumbnail = {
  videoId: string;
  key: string;
  type: DesktopProductMediaGalleryThumbType.Video;
  thumb?: string;
};

export type ProductMediaGalleryThumbnail = ImageThumbnail | VideoThumbnail;

type AlternateImage = NonNullable<
  DesktopProductMediaGalleryVariantFragment['alternateImages'][number]
>;

export const DESKTOP_PRODUCT_MEDIA_GALLERY_VARIANT_FRAGMENT = gql`
  fragment DesktopProductMediaGalleryVariantFragment on VariantData {
    id
    primaryImage: primary_image_urls {
      thumb
      large
      raw
      small
    }
    alternateImages: alternate_image_urls {
      thumb
      large
      raw
      small
    }
  }
`;

export const DESKTOP_PRODUCT_MEDIA_GALLERY_FRAGMENT = gql`
  fragment DesktopProductMediaGalleryFragment on Variant {
    id
    name
    masterVariant: master_variants_data {
      ...DesktopProductMediaGalleryVariantFragment
    }
    otherVariants: other_variants_data {
      ...DesktopProductMediaGalleryVariantFragment
    }
    ...ProductZoomedGalleryFragment
    ...ProductVideosFragment
  }
  ${DESKTOP_PRODUCT_MEDIA_GALLERY_VARIANT_FRAGMENT}
  ${PRODUCT_ZOOMED_GALLERY_FRAGMENT}
  ${PRODUCT_VIDEOS_FRAGMENT}
`;

export interface Props {
  variant: DesktopProductMediaGalleryFragment;
}

export enum Mode {
  Resting,
  Zoomed
}

interface State {
  cursor: number;
  mode: Mode;
}

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const hasMoreThanSixImages = (images: AlternateImage[]) => images.length > 6;

export type Action =
  { type: 'SELECT'; payload: { cursor: number } } | { type: 'ZOOM' } | { type: 'CLOSE' };

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SELECT':
      return { ...state, cursor: action.payload.cursor };
    case 'ZOOM':
      return { ...state, mode: Mode.Zoomed };
    case 'CLOSE':
      return { ...state, mode: Mode.Resting };
  }
};

export const DesktopProductMediaGallery: React.FC<Props> = memo(({ variant }) => {
  const [state, dispatch] = useReducer(reducer, { cursor: 0, mode: Mode.Resting });

  const activeVariant = useMemo(() => getActiveVariantBy(variant, 'id', variant.id), [variant]);

  const [images, { filter: filterImages }] = useList(
    useMemo(
      () => compact([{ ...activeVariant.primaryImage }, ...activeVariant.alternateImages]),
      [activeVariant]
    )
  );

  const imageThumbnails: ImageThumbnail[] = useDesktopProductMediaGalleryImageThumbnails(images);

  const videoThumbnails: VideoThumbnail[] =
    variant?.videos?.map(video => ({
      videoId: video.id,
      key: video.id,
      type: DesktopProductMediaGalleryThumbType.Video as const
    })) || [];

  const thumbnails: ProductMediaGalleryThumbnail[] = [
    ...imageThumbnails.slice(0, VIDEO_THUMBNAIL_POSITION),
    ...videoThumbnails,
    ...imageThumbnails.slice(VIDEO_THUMBNAIL_POSITION)
  ];

  const pagetype = usePageType();

  const handleZoom = useCallback(() => dispatch({ type: 'ZOOM' }), []);
  const handleZoomKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleZoom();
      }
    },
    [handleZoom]
  );
  const handleClose = useCallback(() => dispatch({ type: 'CLOSE' }), []);

  const handleKeydown = useCallback(
    (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowRight':
          dispatch({ type: 'SELECT', payload: { cursor: state.cursor + 1 } });
          break;
        case 'ArrowLeft':
          dispatch({ type: 'SELECT', payload: { cursor: state.cursor - 1 } });
          break;
        default:
          break;
      }
    },
    [state.cursor]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeydown);

    return () => window.removeEventListener('keydown', handleKeydown);
  }, [handleKeydown]);

  const activeIndex = useMemo(
    () => mapCursorToMax(state.cursor, thumbnails.length),
    [thumbnails.length, state.cursor]
  );

  const activeThumbnail = thumbnails[activeIndex];

  const trackImageSelected = useCallback(() => {
    tc.track('Alternate Image Selected', {
      addToScopeOnce: { variant, activeVariant },
      addToTrackingEvent: { pagetype, imageIndex: activeIndex }
    });
  }, [variant, activeVariant, activeIndex, pagetype]);

  const trackVideoSelected = useCallback(() => {
    tc.track('Video Selected', {
      addToScopeOnce: { variant, activeVariant },
      addToTrackingEvent: { pagetype, imageIndex: activeIndex }
    });
  }, [activeIndex, activeVariant, pagetype, variant]);

  const handleSelect = useCallback(
    (cursor: number) => {
      dispatch({ type: 'SELECT', payload: { cursor } });

      if (cursor < imageThumbnails.length) {
        trackImageSelected();

        return;
      }

      trackVideoSelected();
    },
    [imageThumbnails.length, trackImageSelected, trackVideoSelected]
  );

  const { matches: isMobile } = useBreakpoint({ lt: 'md' });

  const moreThanSixImages = hasMoreThanSixImages(images);
  const [seeAllLink, setSeeAllLink] = useState(!isMobile && moreThanSixImages);

  return (
    <>
      <div className="DesktopProductMediaGallery">
        <>
          <div
            className={classNames('DesktopProductMediaGallery__selector', {
              'DesktopProductMediaGallery__selector--wide': !isMobile
            })}
          >
            <div
              className={classNames('DesktopProductMediaGallery__images-only', {
                'DesktopProductMediaGallery__images-only--wide': seeAllLink
              })}
            >
              {thumbnails.map((thumb, index) => (
                <React.Fragment key={thumb.key}>
                  {thumb.type === DesktopProductMediaGalleryThumbType.Image && (
                    <DesktopProductMediaGalleryImageThumb
                      src={thumb.src || PENDING_SRC}
                      alt={variant.name || 'Product view'}
                      index={index}
                      onClick={handleSelect}
                      onMouseEnter={handleSelect}
                      onError={() => filterImages(image => image.thumb !== thumb.src)}
                      selected={activeIndex === index}
                    />
                  )}

                  {thumb.type === DesktopProductMediaGalleryThumbType.Video && (
                    <DesktopProductMediaGalleryVideoThumb
                      videoId={thumb.videoId}
                      onMouseEnter={handleSelect}
                      alt={variant.name || 'Product view'}
                      index={index}
                      onClick={handleSelect}
                      selected={activeIndex === index}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>

            {!isMobile &&
              moreThanSixImages &&
              (seeAllLink ? (
                <Clickable
                  className="DesktopProductMediaGallery__see-all"
                  onClick={() => setSeeAllLink(!seeAllLink)}
                >
                  See All
                </Clickable>
              ) : (
                <Clickable
                  className="DesktopProductMediaGallery__see-all"
                  onClick={() => setSeeAllLink(!seeAllLink)}
                >
                  See Less
                </Clickable>
              ))}
          </div>

          {activeThumbnail.type === DesktopProductMediaGalleryThumbType.Image && (
            <div className="DesktopProductMediaGallery__viewer">
              <div
                role="button"
                tabIndex={0}
                aria-label="Zoom image"
                className={classNames('DesktopProductMediaGallery__focus', {
                  'DesktopProductMediaGallery__focus--wide': !isMobile
                })}
                onClick={handleZoom}
                onKeyDown={handleZoomKeyDown}
              >
                <Image
                  className="DesktopProductMediaGallery__image"
                  src={activeThumbnail.large || PENDING_SRC}
                  alt={variant.name || 'Product view'}
                  width={700}
                  height={600}
                  lazyload
                />
              </div>
            </div>
          )}

          {activeThumbnail.type === DesktopProductMediaGalleryThumbType.Video && (
            <div className="DesktopProductMediaGallery__viewer">
              <div
                className={classNames('DesktopProductMediaGallery__focus', {
                  'DesktopProductMediaGallery__focus--wide': !isMobile
                })}
              >
                <ProductDetailPageVideo videoId={activeThumbnail.key} videos={variant.videos} />
              </div>
            </div>
          )}
        </>
      </div>

      {state.mode === Mode.Zoomed && (
        <ProductZoomedGallery
          onClose={handleClose}
          images={images}
          thumbnails={thumbnails}
          activeIndex={activeIndex}
        />
      )}
    </>
  );
});
